import { EoaDistrictPayload, State } from '../../services/eoaConfigServices';

export type DsaSigned = 'yes' | 'no' | '';

export interface DistrictInformationForm {
  stateId: string;
  districtName: string;
  districtAliases: string;
  sis: string;
  lms: string;
  dsaSigned: DsaSigned;
}

const INITIAL_DISTRICT_INFORMATION_FORM: DistrictInformationForm = {
  stateId: '',
  districtName: '',
  districtAliases: '',
  sis: '',
  lms: '',
  dsaSigned: '',
};

const parseDistrictAliases = (value: string): string[] =>
  value
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean);

const formatDistrictAliases = (aliases: string[] | undefined): string => (aliases ?? []).join('\n');

const DISTRICT_NAME_MIN_LENGTH = 3;
const DISTRICT_NAME_MAX_LENGTH = 50;

const emptyToNull = (value: string): string | null => value.trim() || null;

const districtNameError = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed.length < DISTRICT_NAME_MIN_LENGTH) {
    return `Enter at least ${DISTRICT_NAME_MIN_LENGTH} characters.`;
  }
  if (trimmed.length > DISTRICT_NAME_MAX_LENGTH) {
    return `District name cannot exceed ${DISTRICT_NAME_MAX_LENGTH} characters.`;
  }
  if (/^[0-9_-]/.test(trimmed)) {
    return 'District name cannot start with a number, hyphen, or underscore.';
  }
  const slug = trimmed.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (slug.length < DISTRICT_NAME_MIN_LENGTH || !/^[a-z]/.test(slug)) {
    return 'District name must include at least 3 letters or numbers and start with a letter.';
  }
  return null;
};

const isAcceptableDistrictName = (value: string): boolean => districtNameError(value) === null && value.trim() !== '';

const hasInvalidDistrictInformation = (form: DistrictInformationForm): boolean =>
  districtNameError(form.districtName) !== null;

const toEoaDistrictPayload = (form: DistrictInformationForm): EoaDistrictPayload => ({
  district_name: emptyToNull(form.districtName),
  district_state_id: form.stateId || null,
  district_aliases: parseDistrictAliases(form.districtAliases),
  district_sis: emptyToNull(form.sis),
  district_lms: emptyToNull(form.lms),
  is_dsa_signed: form.dsaSigned === 'yes' ? true : form.dsaSigned === 'no' ? false : null,
});

const districtInformationFromEoa = (eoa: Partial<EoaDistrictPayload>): DistrictInformationForm => ({
  stateId: eoa.district_state_id ?? '',
  districtName: eoa.district_name ?? '',
  districtAliases: formatDistrictAliases(eoa.district_aliases),
  sis: eoa.district_sis ?? '',
  lms: eoa.district_lms ?? '',
  dsaSigned: eoa.is_dsa_signed === true ? 'yes' : eoa.is_dsa_signed === false ? 'no' : '',
});

const extractCreatedEoaId = (data: unknown): string => {
  if (typeof data === 'string' && data.length > 0) return data;
  if (data && typeof data === 'object') {
    if ('id' in data && typeof data.id === 'string' && data.id.length > 0) return data.id;
    if ('eoa_id' in data && typeof data.eoa_id === 'string' && data.eoa_id.length > 0) return data.eoa_id;
  }
  throw new Error('EOA create response did not include an id');
};

const areAliasesEqual = (left: string[], right: string[]): boolean => JSON.stringify(left) === JSON.stringify(right);

const changedDistrictPayload = (
  previous: DistrictInformationForm,
  next: DistrictInformationForm
): EoaDistrictPayload => {
  const previousPayload = toEoaDistrictPayload(previous);
  const nextPayload = toEoaDistrictPayload(next);
  const patch: EoaDistrictPayload = {};

  const nextNameIsSaveable = districtNameError(next.districtName) === null;
  if (nextNameIsSaveable && previousPayload.district_name !== nextPayload.district_name) {
    patch.district_name = nextPayload.district_name;
  }
  if (previousPayload.district_state_id !== nextPayload.district_state_id) {
    patch.district_state_id = nextPayload.district_state_id;
  }
  if (!areAliasesEqual(previousPayload.district_aliases ?? [], nextPayload.district_aliases ?? [])) {
    patch.district_aliases = nextPayload.district_aliases;
  }
  if (previousPayload.district_sis !== nextPayload.district_sis) {
    patch.district_sis = nextPayload.district_sis;
  }
  if (previousPayload.district_lms !== nextPayload.district_lms) {
    patch.district_lms = nextPayload.district_lms;
  }
  if (previousPayload.is_dsa_signed !== nextPayload.is_dsa_signed) {
    patch.is_dsa_signed = nextPayload.is_dsa_signed;
  }

  return patch;
};

const hasDistrictInformationContent = (form: DistrictInformationForm): boolean =>
  form.stateId !== '' ||
  form.districtName.trim() !== '' ||
  parseDistrictAliases(form.districtAliases).length > 0 ||
  form.sis.trim() !== '' ||
  form.lms.trim() !== '' ||
  form.dsaSigned !== '';

const persistDistrictInformation = async ({
  form,
  previousForm,
  eoaId,
  createEoa,
  patchEoa,
  createIfEmpty = false,
}: {
  form: DistrictInformationForm;
  previousForm: DistrictInformationForm;
  eoaId: string | null;
  createEoa: (payload: EoaDistrictPayload) => Promise<unknown>;
  patchEoa: (eoaId: string, payload: EoaDistrictPayload) => Promise<unknown>;
  createIfEmpty?: boolean;
}): Promise<string | null> => {
  if (!eoaId) {
    if (!hasDistrictInformationContent(form) && !createIfEmpty) return null;
    return extractCreatedEoaId(await createEoa(changedDistrictPayload(INITIAL_DISTRICT_INFORMATION_FORM, form)));
  }

  const payload = changedDistrictPayload(previousForm, form);
  if (Object.keys(payload).length === 0) return eoaId;

  await patchEoa(eoaId, payload);
  return eoaId;
};

const filterStates = (states: State[], query: string): State[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return states;

  return states.filter((state) => {
    const name = state.name.toLowerCase();
    const shortName = state.short_name.toLowerCase();
    return name.includes(normalizedQuery) || shortName.includes(normalizedQuery);
  });
};

export {
  DISTRICT_NAME_MAX_LENGTH,
  DISTRICT_NAME_MIN_LENGTH,
  INITIAL_DISTRICT_INFORMATION_FORM,
  changedDistrictPayload,
  districtInformationFromEoa,
  districtNameError,
  extractCreatedEoaId,
  filterStates,
  hasDistrictInformationContent,
  hasInvalidDistrictInformation,
  isAcceptableDistrictName,
  persistDistrictInformation,
  toEoaDistrictPayload,
};
